<script lang="ts" setup>
    /**
     * The icon button of the chat dock: attach, send and stop in the input,
     * mute and hang up in a live call. One component, because the call bar
     * takes the place of the input and the two must not look like two
     * different pieces of software.
     *
     * Native attributes fall through to the button, so `title`, `disabled` and
     * `aria-pressed` are written by the caller as they would be on a `<button>`.
     */
    withDefaults(
        defineProps<{
            icon: string
            /** Written next to the icon, for an action nobody should have to guess from a glyph. */
            label?: string
            type?: 'button' | 'submit'
            modifiers?: 'danger'
        }>(),
        { label: undefined, type: 'button', modifiers: undefined },
    )
</script>

<template>
    <button
        class="pk-chat-button"
        :class="[
            modifiers ? `pk-chat-button--${modifiers}` : undefined,
            { 'pk-chat-button--labelled': label },
        ]"
        :type="type">
        <VvIcon :name="icon" class="pk-chat-button__icon" />
        <span v-if="label" class="pk-chat-button__label">{{ label }}</span>
    </button>
</template>

<style lang="scss">
    .pk-chat-button {
        display: inline-flex;
        align-items: center;
        flex-shrink: 0;
        padding: var(--spacing-4);
        border: none;
        border-radius: var(--rounded);
        background-color: transparent;
        color: var(--color-word-1);
        cursor: pointer;
        transition: var(--transition-colors);

        &:hover {
            background-color: var(--color-surface-2);
        }

        &:focus-visible {
            outline: 1px solid var(--color-surface-5);
            outline-offset: 0;
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.5;
        }

        // A toggle has to survive a glance: a crossed-out glyph alone changes
        // too few pixels to notice mid-sentence.
        &[aria-pressed='true'] {
            background-color: var(--color-surface-3);
            color: var(--color-word);
        }

        // A wash of the same red rather than a lighter red behind it: the
        // `lighten-5` step lands close enough to the icon to leave it at about
        // 2:1, which is a button that disappears exactly while it is being
        // pointed at. The tint composites over whatever surface is behind, so
        // it holds in the dark theme too.
        &--danger {
            color: var(--color-danger);

            &:hover {
                background-color: color-mix(
                    in srgb,
                    var(--color-danger) 12%,
                    transparent
                );
            }
        }

        &--labelled {
            gap: var(--spacing-4);
            padding-inline: var(--spacing-8);
            font-size: var(--text-12);
        }

        &__icon {
            display: block;
            width: var(--spacing-20);
            height: var(--spacing-20);
        }
    }
</style>
